/**
 * =============================================================================
 * ExecuteStepBody
 * Versão: 1.0.0
 *
 * Descrição:
 *   Corpo (sem Sheet/Dialog) reutilizável da execução de um step de cadência.
 *   Renderiza: cabeçalho com tipo/título, atalhos rápidos (WhatsApp/tel/mailto/copiar),
 *   roteiro renderizado com variáveis, critérios de sucesso e objeções.
 *
 *   Foi extraído de ExecuteStepSheet para permitir embedar o mesmo componente
 *   tanto em um Sheet (vendedor abre na aba Cadência) quanto em um painel
 *   inline dentro do ActivityDetailsModalPremium.
 *
 * Observações de segurança:
 *   - HTML é sanitizado via `renderScript` (DOMPurify) antes de ser injetado.
 *
 * Release notes:
 *   v1.0.0 - Criação a partir do ExecuteStepSheet (refatoração para reuso)
 * =============================================================================
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { FaqAccordion } from '@/components/shared/FaqAccordion';
import {
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  Calendar,
  CheckSquare,
  StickyNote,
  ListChecks,
  Copy,
  Check,
  Pencil,
  RotateCcw,
  Target,
  ScrollText,
  CheckCircle2,
  SkipForward,
  MessageSquareWarning,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { renderScript, renderScriptAsText, type ScriptRenderContext } from '@/lib/playbookScript';
import { useDealScriptContext } from '@/hooks/useDealScriptContext';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import type { CadenceStep } from '@/hooks/useDealCadence';

// Mapa de ícones por tipo de atividade — alinhado ao DealCadenceTab/ExecuteStepSheet
const activityIcon: Record<string, typeof Phone> = {
  call: Phone,
  email: Mail,
  whatsapp: MessageSquare,
  meeting: Calendar,
  task: CheckSquare,
  note: StickyNote,
  visit: MapPin,
};

/** Contexto real do deal (contato/valor/vendedor/tenant) para popular variáveis. */
export interface ExecuteStepDealContext {
  /** ID do deal — necessário para enriquecer variáveis (produtos, contrato, cobrança...) */
  dealId?: string | null;
  contact?: {
    name?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  deal?: {
    title?: string | null;
    value?: number | null;
    currency?: string | null;
    phase?: string | null;
    pipeline?: string | null;
  } | null;
  seller?: {
    full_name?: string | null;
  } | null;
  tenant?: {
    name?: string | null;
  } | null;
}

interface ExecuteStepBodyProps {
  step: CadenceStep;
  context: ExecuteStepDealContext;
  isBusy?: boolean;
  canSkip?: boolean;
  /** Quando true, esconde o cabeçalho interno (útil quando o container já tem header próprio) */
  hideHeader?: boolean;
  /** Quando true, esconde o footer fixo de ações (Concluir/Pular) */
  hideFooter?: boolean;
  /** Quando true, remove o scroll interno e a altura fixa. */
  disableInternalScroll?: boolean;
  /** Critérios marcados (controlado). Se ausente, componente usa estado interno. */
  checkedCriteria?: Record<string, boolean>;
  /** Callback quando o usuário marca/desmarca critérios (modo controlado). */
  onCriteriaChange?: (next: Record<string, boolean>) => void;
  onComplete?: (stepId: string) => void;
  onSkip?: (stepId: string) => void;
}


/** Normaliza telefone para o formato esperado pelo WhatsApp (apenas dígitos). */
function normalizePhone(raw?: string | null): string {
  if (!raw) return '';
  const digits = raw.replace(/\D+/g, '');
  return digits.replace(/^0+/, '');
}

/** Extrai o primeiro nome a partir de um nome completo. */
function firstName(full?: string | null): string {
  return (full ?? '').trim().split(/\s+/)[0] ?? '';
}

/**
 * Corpo reutilizável de execução do step (Receita Previsível).
 */
export function ExecuteStepBody({
  step,
  context,
  isBusy = false,
  canSkip = false,
  hideHeader = false,
  hideFooter = false,
  disableInternalScroll = false,
  checkedCriteria: controlledCriteria,
  onCriteriaChange,
  onComplete,
  onSkip,
}: ExecuteStepBodyProps) {
  const { toast } = useToast();
  // Estado interno é usado APENAS quando o pai não passa checkedCriteria (modo não controlado).
  const [internalCriteria, setInternalCriteria] = useState<Record<string, boolean>>({});
  const isControlled = controlledCriteria !== undefined;
  const checkedCriteria = isControlled ? (controlledCriteria as Record<string, boolean>) : internalCriteria;
  const setCriterion = (id: string, value: boolean) => {
    const next = { ...checkedCriteria, [id]: value };
    if (isControlled) {
      onCriteriaChange?.(next);
    } else {
      setInternalCriteria(next);
    }
  };

  // Feedback visual do botão Copiar
  const [copied, setCopied] = useState(false);
  // Edição inline do roteiro (override local em memória, sem persistir no banco)
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState<string | null>(null);

  // Estado "travado": atividade concluída ou pulada — impede edições no painel.
  const isLocked = step?.step_status === 'completed' || step?.step_status === 'skipped';

  // Reseta overrides quando troca de step. Critérios só são resetados no
  // modo NÃO controlado — no controlado, o pai gerencia por stepId.
  React.useEffect(() => {
    if (!isControlled) setInternalCriteria({});
    setEditedText(null);
    setIsEditing(false);
    setCopied(false);
  }, [step?.step_id, isControlled]);

  // Se a atividade travar (concluir/pular) enquanto o usuário está editando,
  // encerra o modo edição para evitar UI inconsistente.
  React.useEffect(() => {
    if (isLocked && isEditing) setIsEditing(false);
  }, [isLocked, isEditing]);


  // Carrega contexto enriquecido (produtos, contrato, cobrança, parcelas)
  // a partir do dealId via RPC. Devolve {} enquanto não há dados.
  const { data: enriched } = useDealScriptContext(context.dealId);

  // Monta contexto de variáveis combinando o contexto local + enriquecido
  const renderCtx: ScriptRenderContext = useMemo(() => {
    const fullName = enriched?.contact?.full_name ?? context.contact?.name ?? '';
    const phoneRaw = enriched?.contact?.phone ?? context.contact?.phone ?? null;
    const phoneE164 = normalizePhone(phoneRaw);
    return {
      contact: {
        first_name: firstName(fullName) || null,
        full_name: fullName || null,
        phone: phoneRaw,
        phone_e164: phoneE164 || null,
        whatsapp_link: phoneE164 ? `https://wa.me/${phoneE164}` : null,
        email: enriched?.contact?.email ?? context.contact?.email ?? null,
        document: enriched?.contact?.document ?? null,
        city: enriched?.contact?.city ?? null,
        state: enriched?.contact?.state ?? null,
      },
      deal: {
        title: enriched?.deal?.title ?? context.deal?.title ?? null,
        value: enriched?.deal?.value ?? context.deal?.value ?? null,
        phase: enriched?.deal?.phase ?? context.deal?.phase ?? null,
        pipeline: enriched?.deal?.pipeline ?? context.deal?.pipeline ?? null,
        created_at: enriched?.deal?.created_at ?? null,
        expected_close_date: enriched?.deal?.expected_close_date ?? null,
        source: enriched?.deal?.source ?? null,
      },
      products: enriched?.products ?? null,
      contract: enriched?.contract ?? undefined,
      charge: enriched?.charge ?? undefined,
      installments: enriched?.installments ?? undefined,
      seller: {
        full_name: enriched?.seller?.full_name ?? context.seller?.full_name ?? null,
        first_name: firstName(enriched?.seller?.full_name ?? context.seller?.full_name) || null,
        email: enriched?.seller?.email ?? null,
        phone: enriched?.seller?.phone ?? null,
      },
      tenant: {
        name: enriched?.tenant?.name ?? context.tenant?.name ?? null,
        support_email: enriched?.tenant?.support_email ?? null,
      },
    };
  }, [context, enriched]);

  const Icon = activityIcon[step.activity_type] ?? ListChecks;

  // Texto puro do script (para copiar / pré-preencher WhatsApp)
  // Considera override de edição inline (editedText) quando presente.
  const defaultScriptText = renderScriptAsText(step.script_html, renderCtx);
  const defaultScriptHtml = renderScript(step.script_html, renderCtx);
  const scriptText = editedText ?? defaultScriptText;
  // Quando editado, exibimos o texto puro envolvido em parágrafos (e re-sanitizado).
  const scriptHtml = editedText !== null
    ? renderScript(
        editedText
          .split(/\n{2,}/)
          .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
          .join(''),
        renderCtx,
      )
    : defaultScriptHtml;

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(scriptText || '');
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: 'Não foi possível copiar', variant: 'destructive' });
    }
  };

  // Valida critérios obrigatórios antes de concluir (aviso, não bloqueio rígido)
  const requiredCriteria = (step.success_criteria ?? []).filter((c) => c.required);
  const missingRequired = requiredCriteria.filter((c) => !checkedCriteria[c.id]);

  const handleComplete = () => {
    if (!onComplete) return;
    if (missingRequired.length > 0) {
      const ok = window.confirm(
        `Existem ${missingRequired.length} critério(s) obrigatório(s) sem marcação. Concluir mesmo assim?`,
      );
      if (!ok) return;
    }
    onComplete(step.step_id);
  };

  return (
    <div className={disableInternalScroll ? 'flex flex-col' : 'flex flex-col h-full min-h-0'}>
      {/* Cabeçalho opcional */}
      {!hideHeader && (
        <div className="px-5 py-3 border-b">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon className="h-4 w-4" />
            <span className="uppercase tracking-wide">{step.activity_type}</span>
            {step.channel && (
              <Badge variant="outline" className="text-[10px] h-4 px-1">
                {step.channel}
              </Badge>
            )}
            {step.is_required && (
              <Badge variant="secondary" className="text-[10px] h-4 px-1">
                Obrigatório
              </Badge>
            )}
          </div>
          <h3 className="text-base font-semibold mt-1">{step.title}</h3>
          {context.contact?.name && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Contato: <span className="font-medium">{context.contact.name}</span>
              {context.contact.phone ? ` · ${formatPhoneForDisplay(context.contact.phone)}` : ''}
            </p>
          )}
        </div>
      )}

      {/* Corpo rolável */}
      <div className={disableInternalScroll ? 'px-5 py-4 space-y-5' : 'flex-1 overflow-y-auto px-5 py-4 space-y-5 scrollbar-thin'}>
        {/* Objetivo */}
        {step.goal && (
          <section>
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <Target className="h-3.5 w-3.5 text-primary" />
              Objetivo
            </div>
            <div className="rounded-lg border bg-accent/30 p-3">
              <p className="text-sm">{step.goal}</p>
            </div>
          </section>
        )}

        {/* Atalhos rápidos (WhatsApp / Ligar / E-mail) ocultos até implementação. */}

        {/* Descrição da atividade (steps manuais) — só aparece quando não há roteiro de playbook */}
        {!step.script_html && step.description_html && (
          <section>
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <ScrollText className="h-3.5 w-3.5 text-primary" />
              Descrição da atividade
            </div>
            <div className="rounded-lg border bg-accent/30 p-3">
              {/* Textarea salva texto puro; preservamos quebras de linha. */}
              <p className="text-sm whitespace-pre-wrap break-words">
                {step.description_html}
              </p>
            </div>
          </section>
        )}

        {/* Roteiro sugerido — alinhado ao card de Descrição da aba Resumo */}
        {step.script_html && (
          <section>
            {/* Título no padrão das outras seções da cadência */}
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <ScrollText className="h-3.5 w-3.5 text-primary" />
              Roteiro sugerido
            </div>

            {/* Card no mesmo chrome de DealDescriptionAndAttachments */}
            <div className="rounded-lg border bg-accent/30">
              {/* Header com ações (somente ícones + tooltip) */}
              <div className="flex items-center justify-end p-3 pb-2 gap-0.5">
                {/* Restaurar padrão — só quando há override manual */}
                {editedText !== null && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={isLocked}
                    onClick={() => {
                      setEditedText(null);
                      setIsEditing(false);
                    }}
                    title={isLocked ? 'Atividade concluída' : 'Restaurar padrão'}
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}
                {/* Editar / Concluir edição — desabilitado quando atividade travada */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={isLocked}
                  onClick={() => {
                    if (isLocked) return;
                    if (!isEditing) {
                      setEditedText((prev) => prev ?? defaultScriptText);
                    }
                    setIsEditing((v) => !v);
                  }}
                  title={isLocked ? 'Atividade concluída' : (isEditing ? 'Concluir edição' : 'Editar roteiro')}
                >
                  {isEditing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                </Button>
                {/* Copiar — sempre permitido, mesmo em atividade concluída */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={copyScript}
                  title={copied ? 'Copiado' : 'Copiar roteiro'}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
              </div>

              {/* Conteúdo */}
              <div className="px-3 pb-3">
                {isEditing && !isLocked ? (
                  <Textarea
                    value={editedText ?? ''}
                    onChange={(e) => setEditedText(e.target.value)}
                    className="min-h-[180px] text-sm leading-relaxed bg-background"
                    placeholder="Edite o roteiro como desejar..."
                  />
                ) : (
                  <div className="max-h-[280px] overflow-y-auto scrollbar-thin">
                    <RichTextEditor
                      content={scriptHtml}
                      readOnly
                      showToolbar={false}
                      minHeight="60px"
                    />
                  </div>
                )}
              </div>

            </div>
          </section>
        )}

        {/* Critérios de sucesso */}
        {step.success_criteria && step.success_criteria.length > 0 && (
          <section>
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <CheckSquare className="h-3.5 w-3.5 text-primary" />
              Critérios de sucesso
            </div>
            <ul className="space-y-2 rounded-md border p-3">
              {step.success_criteria.map((c) => {
                const checked = !!checkedCriteria[c.id];
                return (
                  <li key={c.id} className="flex items-start gap-2 text-sm">
                    <Checkbox
                      id={`sc-${c.id}`}
                      checked={checked}
                      disabled={isLocked}
                      onCheckedChange={(v) => setCriterion(c.id, !!v)}
                      className="mt-0.5"
                    />
                    <label
                      htmlFor={`sc-${c.id}`}
                      className={cn(
                        'flex-1 leading-snug',
                        isLocked ? 'cursor-not-allowed' : 'cursor-pointer',
                        checked && 'line-through text-muted-foreground',
                      )}
                    >
                      {c.label}
                      {c.required && (
                        <Badge variant="outline" className="ml-1.5 text-[9px] h-4 px-1">
                          obrig.
                        </Badge>
                      )}
                    </label>
                  </li>

                );
              })}
            </ul>
          </section>
        )}

        {/* Objeções */}
        {step.objections && step.objections.length > 0 && (
          <section>
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
              <MessageSquareWarning className="h-3.5 w-3.5 text-primary" />
              Objeções comuns
            </div>
            <FaqAccordion
              type="multiple"
              copyable
              items={step.objections.map((o, idx) => ({
                id: o.id,
                question: `${idx + 1} — ${o.question}`,
                answerHtml: renderScript(o.answer_html, renderCtx),
              }))}
            />

          </section>
        )}
      </div>

      {/* Footer fixo */}
      {!hideFooter && (
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          {canSkip && onSkip && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSkip(step.step_id)}
              disabled={isBusy || isLocked}

              className="gap-1.5"
            >
              <SkipForward className="h-3.5 w-3.5" />
              Pular
            </Button>
          )}
          {onComplete && (
            <Button
              variant="default"
              size="sm"
              onClick={handleComplete}
              disabled={isBusy || isLocked}
              className="gap-1.5"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Concluir atividade
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
